import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import AnimatedPage from './components/AnimatedPage';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import ScrollToTop from './components/ScrollToTop';
import { DashboardSkeleton } from './components/LoadingSkeletons';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';

const BaseballPage          = React.lazy(() => import(/* webpackChunkName: "sport-baseball"   */ './pages/BaseballPage'));
const BasketballPage        = React.lazy(() => import(/* webpackChunkName: "sport-basketball" */ './pages/BasketballPage'));
const FootballPage          = React.lazy(() => import(/* webpackChunkName: "sport-football"   */ './pages/FootballPage'));
const BaseballLineupsPage   = React.lazy(() => import(/* webpackChunkName: "sport-baseball"   */ './pages/BaseballLineupsPage'));
const BasketballLineupsPage = React.lazy(() => import(/* webpackChunkName: "sport-basketball" */ './pages/BasketballLineupsPage'));
const FootballLineupsPage   = React.lazy(() => import(/* webpackChunkName: "sport-football"   */ './pages/FootballLineupsPage'));
const CrossSportPage        = React.lazy(() => import('./pages/CrossSportPage'));
const PlaybookPage          = React.lazy(() => import('./pages/PlaybookPage'));
const SecuritySettingsPage  = React.lazy(() => import('./pages/SecuritySettingsPage'));
const AuditLogPage          = React.lazy(() => import(/* webpackChunkName: "admin" */ './pages/AuditLogPage'));
const UsersPage             = React.lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/UsersPage'));
const RolesPage             = React.lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/RolesPage'));
const OverviewPage          = React.lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/OverviewPage'));
const HealthPage            = React.lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/HealthPage'));
const ContestHistoryPage    = React.lazy(() => import(/* webpackChunkName: "dfs" */ './components/ContestHistory'));

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<DashboardSkeleton />}>
        <Routes location={location} key={location.pathname}>
          {/* Login - no auth required */}
          <Route path="/login" element={<AnimatedPage><LoginPage /></AnimatedPage>} />

          {/* All other routes require auth */}
          <Route path="/" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><HomePage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Baseball */}
          <Route path="/baseball" element={<Navigate to="/baseball/betting" replace />} />
          <Route path="/baseball/betting" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BaseballPage section="betting" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/baseball/dfs" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BaseballPage section="dfs" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/baseball/lineups/:contestId" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BaseballLineupsPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Basketball */}
          <Route path="/basketball" element={<Navigate to="/basketball/betting" replace />} />
          <Route path="/basketball/betting" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BasketballPage section="betting" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/basketball/dfs" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BasketballPage section="dfs" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/basketball/lineups/:contestId" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><BasketballLineupsPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Football */}
          <Route path="/football" element={<Navigate to="/football/betting" replace />} />
          <Route path="/football/betting" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><FootballPage section="betting" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/football/dfs" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><FootballPage section="dfs" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/football/lineups/:contestId" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><FootballLineupsPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          <Route path="/analytics" element={<ProtectedRoute requiredPermission="Analytics.View"><AnimatedPage><ErrorBoundary><CrossSportPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Contest History */}
          <Route path="/contests/history" element={<ProtectedRoute requiredPermission="Contests.History.View"><AnimatedPage><ErrorBoundary><ContestHistoryPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Playbook */}
          <Route path="/playbook/baseball" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><PlaybookPage sport="baseball" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/playbook/basketball" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><PlaybookPage sport="basketball" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/playbook/football" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><PlaybookPage sport="football" /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />

          {/* Settings & Admin */}
          <Route path="/settings/security" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><SecuritySettingsPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AnimatedPage><ErrorBoundary><OverviewPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requiredPermission="Admin.Users.Read"><AnimatedPage><ErrorBoundary><UsersPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute requiredPermission="Admin.Roles.Read"><AnimatedPage><ErrorBoundary><RolesPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/admin/audit-log" element={<ProtectedRoute requiredPermission="Admin.AuditLog.Read"><AnimatedPage><ErrorBoundary><AuditLogPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
          <Route path="/admin/health" element={<ProtectedRoute requiredPermission="Admin.Health.Read"><AnimatedPage><ErrorBoundary><HealthPage /></ErrorBoundary></AnimatedPage></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

function AppLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const showSidebar = isAuthenticated && !isLoginPage && !isLoading;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <ScrollToTop />
      {showSidebar && <Sidebar />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          // Do NOT add `overflowX: 'hidden'` here. Per the CSS Overflow spec,
          // setting overflow-x to a non-visible value while overflow-y is
          // visible causes overflow-y to compute to `auto` — which makes
          // <main> a vertical scroll container and traps iOS Safari touch
          // gestures inside it instead of routing them to body scroll.
          // Horizontal overflow is prevented at the component level
          // (TableContainer, flexWrap filter rows, ResponsiveTableCell).
          minWidth: 0,
          // Match the mobile AppBar's top safe-area handling on the bottom so
          // the iPhone home indicator can't visually clip the last item.
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        {showSidebar && (
          <Toolbar
            sx={{
              display: { xs: 'block', md: 'none' },
              // Match the mobile AppBar height + iOS safe area so fixed header
              // doesn't cover page content.
              minHeight: 'calc(56px + env(safe-area-inset-top))',
            }}
          />
        )}
        <AnimatedRoutes />
      </Box>
    </Box>
  );
}

function AppContent() {
  const { theme } = useTheme();

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppLayout />
        </AuthProvider>
      </Router>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
